/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { Outlet, useLocation } from 'react-router-dom';

import { AuthenticatedGuard } from '@/AuthenticatedGuard';
import { EnvironmentBadge } from '@/components/EnvironmentBadge';
import { JobBanners } from '@/components/JobBanners/JobBanners';
import { LeftMenu } from '@/components/menus/LeftMenu';
import { MimicErrorBoundary } from '@/components/MimicErrorBoundary';
import { MimicLogo } from '@/components/MimicLogo';
import { MainLayoutSider } from '@/components/Siders/MainLayoutSider';
import { SubtenantSwitcher } from '@/components/SubtenantSwitcher/SubtenantSwitcher';
import { SupportFooter } from '@/components/SupportFooter';
import { TenantSwitcher } from '@/components/TenantSwitcher/TenantSwitcher';
import { useTenantChannel } from '@/hooks/useTenantChannel';
import { useTenantSubtenantManager } from '@/hooks/useTenantSubtenantManager';
import { useWebsocket } from '@/hooks/useWebsocket';
import { Flex } from '@/primitives/Flex';
import { Theme } from '@/primitives/Theme';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';
import { mimicEnv } from '@/utils/environments';

import { MobileNavBar } from './components/MobileNavBar/MobileNavBar';
import { Container } from './primitives/Container';
import { Content } from './primitives/Content';
import { Header } from './primitives/Header';
import { Layout } from './primitives/Layout';
import './MainLayout.css';

export function MainLayout() {
  const { pathname } = useLocation();

  // Use our centralized tenant/subtenant management hook
  const { currentUser, selectedTenantID, selectedSubtenantID, onSelectTenant, onSelectSubtenant } =
    useTenantSubtenantManager();

  // Sidebar state management
  const menuOpen = useMgmtPlaneStateStore((state) => state.menuOpen);
  const setMenuOpen = useMgmtPlaneStateStore((state) => state.setMenuOpen);

  // Initialize global hooks
  useWebsocket();
  useTenantChannel(selectedTenantID);
  const environment = mimicEnv();

  const toggleSidebar = () => {
    setMenuOpen(!menuOpen);
  };

  return (
    <MimicErrorBoundary>
      <AuthenticatedGuard>
        <Layout>
          <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
            <Layout hasSider>
              <Theme>
                <MainLayoutSider>
                  <Container gap="none" justify="space-between" full>
                    <Container.Fixed>
                      <Flex align="center">
                        <Header>
                          <MimicLogo width="6rem" height="2rem" />
                        </Header>
                        <EnvironmentBadge environment={environment} />
                      </Flex>

                      <TenantSwitcher
                        currentUser={currentUser}
                        selectedTenantID={selectedTenantID}
                        onSelectTenant={onSelectTenant}
                      />

                      <SubtenantSwitcher
                        currentUser={currentUser}
                        selectedSubtenantID={selectedSubtenantID}
                        selectedTenantID={selectedTenantID}
                        onSelectSubtenant={onSelectSubtenant}
                      />
                    </Container.Fixed>
                    <Container.Expand>
                      <LeftMenu tenantID={selectedTenantID!} currentUser={currentUser!} currentPath={pathname} />
                    </Container.Expand>
                  </Container>
                </MainLayoutSider>
              </Theme>
              <Content>
                <MobileNavBar toggleSidebar={toggleSidebar} />
                <JobBanners key={`job-banner-${selectedSubtenantID}`} />
                <div className="main-layout-outlet">
                  <Outlet />
                  {!pathname.includes('telemetry-beta') &&
                    !pathname.includes('telemetry-viewer') &&
                    !pathname.includes('api-reference') &&
                    !pathname.includes('change-audit') &&
                    !pathname.includes('bi-dashboard') &&
                    !pathname.includes('plugins') &&
                    !pathname.includes('node-e2e') && <SupportFooter />}
                </div>
              </Content>
            </Layout>
          </div>
        </Layout>
      </AuthenticatedGuard>
    </MimicErrorBoundary>
  );
}
